// DriverTransportsChat.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  FaExclamationTriangle,
  FaSearch,
  FaComments,
  FaPaperclip,
  FaTruck,
  FaBox,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaCommentDots,
} from "react-icons/fa";
import "../clientTable/ClientTable.css";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { driverAPI } from "../../api/apiSystem";
import { useTranslation } from "react-i18next";
import ClientChatBox from "./ClientChatBox";
import DriverChatModal from "../driverTable/DriverChatModal";

const parseOfferCard = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("__OFFER_CARD__")) return null;
  try {
    return JSON.parse(raw.replace("__OFFER_CARD__", ""));
  } catch {
    return null;
  }
};

/** استخراج مدينة مختصرة من عنوان طويل */
const shortPlace = (addr) => {
  if (!addr) return "—";
  if (Array.isArray(addr)) addr = addr[0] || "";
  // "50667 Cologne, DE" أو "DE, 51674 Wiehl"
  const s = String(addr).trim();
  // لو فيه فاصلة خذ آخر جزئين مفيدين
  const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    // رقم بريدي + مدينة أو مدينة + دولة
    return parts.slice(0, 2).join(", ");
  }
  return s.length > 28 ? s.slice(0, 28) + "…" : s;
};

const STATUS_META = {
  rejected: {
    label: "Abgelehnt",
    color: "#dc2626",
    bg: "#fef2f2",
    border: "#fecaca",
    icon: FaTimesCircle,
  },
  accepted: {
    label: "Angenommen",
    color: "#059669",
    bg: "#ecfdf5",
    border: "#a7f3d0",
    icon: FaCheckCircle,
  },
  assigned: {
    label: "Angenommen",
    color: "#059669",
    bg: "#ecfdf5",
    border: "#a7f3d0",
    icon: FaCheckCircle,
  },
  pending: {
    label: "Angebot offen",
    color: "#d97706",
    bg: "#fffbeb",
    border: "#fde68a",
    icon: FaClock,
  },
  inquiry: {
    label: "Neue Nachricht",
    color: "#2563eb",
    bg: "#eff6ff",
    border: "#bfdbfe",
    icon: FaCommentDots,
  },
  default: {
    label: "Nachricht",
    color: "#64748b",
    bg: "#f8fafc",
    border: "#e2e8f0",
    icon: FaCommentDots,
  },
};

export default function DriverTransportsChat() {
  const { t } = useTranslation();

  const [messages, setMessages] = useState([]);
  const [transportsMap, setTransportsMap] = useState({}); // transport_id → transport
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all"); // all | unread

  const [showOrder, setShowOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [ordersUnreadCounts, setOrdersUnreadCounts] = useState({});

  const [userType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // في أعلى الكومبوننت بعد userType
  const [currentUserId] = useState(() => {
    try {
      const raw =
        localStorage.getItem("user") ||
        localStorage.getItem("customer") ||
        localStorage.getItem("authUser");
      if (!raw) return null;
      const u = JSON.parse(raw);
      return u?.id ?? u?.user_id ?? null;
    } catch {
      return null;
    }
  });

  // ── Messages ──────────────────────────────────────────────────────────────
  const fetchTransportsChat = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await driverAPI.transportsOrders.transportsChat();

      if (response?.success) {
        const list =
          response.data?.data?.messages ||
          response.data?.messages ||
          response.data?.data ||
          [];
        setMessages(Array.isArray(list) ? list : []);

        const counts = {};
        (Array.isArray(list) ? list : []).forEach((msg) => {
          const oid = msg.transport_order_id;
          if (!oid) return;
          if (!counts[oid]) counts[oid] = 0;

          // ✅ فقط الرسائل اللي أنا المستقبل فيها + مش مقروءة
          const isForMe =
            currentUserId &&
            (msg.receiver?.id === currentUserId ||
              msg.receiver_id === currentUserId);

          if (!msg.is_read && isForMe) {
            counts[oid] += 1;
          }
        });
        setOrdersUnreadCounts(counts);
      } else {
        setError(response?.message || t("invoices.alerts.fetchError"));
        setMessages([]);
      }
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError(t("invoices.alerts.loadError"));
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Approved transports (للمسارات) ────────────────────────────────────────
  const fetchApprovedTransports = async () => {
    try {
      const res = await driverAPI.transports.index({ per_page: 100 });
      if (!res?.success) return;

      const list =
        res.data?.data?.data ||
        res.data?.data ||
        res.data ||
        [];

      const map = {};
      (Array.isArray(list) ? list : []).forEach((tr) => {
        if (tr?.id) map[tr.id] = tr;
        // لو جوه order
        if (tr?.order?.id) {
          // يمكن نحتاج order لاحقًا
        }
      });
      setTransportsMap(map);
    } catch (err) {
      console.warn("approved transports:", err);
    }
  };

  useEffect(() => {
    fetchTransportsChat();
    fetchApprovedTransports();
  }, []);


  const parseMsgDate = (dateStr) => {
    if (!dateStr) return 0;
    try {
      if (String(dateStr).includes(".")) {
        // dd.mm.yyyy HH:mm:ss
        const [datePart, timePart = "00:00:00"] = String(dateStr).split(" ");
        const [day, month, year] = datePart.split(".");
        const d = new Date(`${year}-${month}-${day}T${timePart}`);
        return isNaN(d.getTime()) ? 0 : d.getTime();
      }
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    } catch {
      return 0;
    }
  };

  // ── Group conversations ───────────────────────────────────────────────────
  const conversations = useMemo(() => {
    const map = new Map();
    const myRole =
      userType === "driver" || userType === "driver_company"
        ? "Driver"
        : "Customer";

    messages.forEach((msg) => {
      const orderId = msg.transport_order_id;
      if (!orderId) return;

      if (!map.has(orderId)) {
        map.set(orderId, {
          orderId,
          bookingId: msg.booking_id || null,
          transportId: msg.transport_id || null,
          messages: [],
          lastMessage: null,
          unreadCount: 0,
          otherParty: null,
          offerStatus: null,
        });
      }

      const conv = map.get(orderId);
      conv.messages.push(msg);

      if (!conv.transportId && msg.transport_id) {
        conv.transportId = msg.transport_id;
      }

      if (!conv.bookingId && msg.booking_id) {
        conv.bookingId = msg.booking_id;
      }

      if (
        !conv.lastMessage ||
        parseMsgDate(msg.created_at) > parseMsgDate(conv.lastMessage.created_at)
      ) {
        conv.lastMessage = msg;
      }

      if (!msg.is_read) conv.unreadCount += 1;

      if (msg.sender?.role !== myRole) {
        conv.otherParty = msg.sender;
      } else if (msg.receiver?.role !== myRole) {
        conv.otherParty = msg.receiver;
      }

      // آخر حالة عرض من offer cards
      const offer = parseOfferCard(msg.message);
      if (offer?.status) {
        conv.offerStatus = offer.status; // pending | accepted | rejected
        if (offer.from) conv.offerFrom = offer.from;
        if (offer.to) conv.offerTo = offer.to;
        if (offer.price) conv.offerPrice = offer.price;
      }
    });

    return Array.from(map.values())
      .map((conv) => {
        const tr = transportsMap[conv.transportId] || null;
        return {
          ...conv,
          transport: tr,
          from:
            conv.offerFrom ||
            tr?.booking_from ||
            tr?.transport?.booking_from ||
            null,
          to:
            conv.offerTo ||
            tr?.booking_to ||
            tr?.transport?.booking_to ||
            null,
          company:
            tr?.user?.company_name ||
            tr?.company_name ||
            conv.otherParty?.company ||
            null,
        };
      })
      .sort((a, b) => {
        const ta = parseMsgDate(a.lastMessage?.created_at);
        const tb = parseMsgDate(b.lastMessage?.created_at);
        return tb - ta;
      });
  }, [messages, userType, transportsMap]);

  const unreadTotal = useMemo(
    () =>
      conversations.reduce(
        (sum, c) => sum + (ordersUnreadCounts[c.orderId] ?? c.unreadCount ?? 0),
        0,
      ),
    [conversations, ordersUnreadCounts],
  );

    const filteredConversations = useMemo(() => {
      let list = conversations;
      if (filter === "unread") {
      list = list.filter(
        (c) => (ordersUnreadCounts[c.orderId] ?? c.unreadCount) > 0,
      );
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((c) => {
        const name = c.otherParty?.name?.toLowerCase() || "";
        const last = c.lastMessage?.message?.toLowerCase() || "";
        const from = String(c.from || "").toLowerCase();
        const to = String(c.to || "").toLowerCase();
        return (
          name.includes(q) ||
          last.includes(q) ||
          from.includes(q) ||
          to.includes(q) ||
          String(c.orderId).includes(q)
        );
      });
    }
    return list;
  }, [conversations, searchTerm, filter, ordersUnreadCounts]);

  // ── Open chat ─────────────────────────────────────────────────────────────
  const handleOpenChat = async (conv) => {
    const transportId = conv.transportId || conv.lastMessage?.transport_id;
    const orderId = conv.orderId;

    // ✅ بس دول المطلوب
    if (!transportId || !orderId) {
      console.error("Missing IDs", { transportId, orderId, conv });
      return;
    }

    const tr = conv.transport || transportsMap[transportId] || {};

    const orderPayload = {
      id: orderId,
      transport_order_id: orderId,
      transport_id: transportId,
      transport: {
        id: transportId,
        booking_from: conv.from || tr.booking_from,
        booking_to: conv.to || tr.booking_to,
        distance: tr.distance,
        ...(tr.transport || tr),
      },
      otherParty: conv.otherParty,
      driver_name:
        conv.otherParty?.role === "Customer" ? conv.otherParty?.name : null,
      price: conv.offerPrice || tr?.order?.price || tr?.price,
      status: conv.offerStatus || tr?.order?.status || tr?.status,
    };

    setSelectedOrder(orderPayload);
    setShowOrder(true);

    // تفاصيل إضافية (اختياري)
    try {
      const res =
        (await driverAPI.transportsOrders.show?.(orderId)) ||
        (await driverAPI.transportsOrders.get?.(transportId, orderId));

      if (res?.success) {
        const full = res.data?.data || res.data;
        setSelectedOrder((prev) => ({
          ...prev,
          ...full,
          id: full.id || orderId,
          transport_id: full.transport_id || full.transport?.id || transportId,
          transport: {
            id: transportId,
            ...(full.transport || {}),
          },
        }));
      }
    } catch (err) {
      console.warn("order details:", err);
    }
  };

  const updateOrderUnreadCount = (orderId, newCount) => {
    setOrdersUnreadCounts((prev) => ({ ...prev, [orderId]: newCount }));
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";

    try {
      let d;

      if (typeof dateStr === "string" && dateStr.includes(".")) {
        // "19.09.2026 23:11:17" → dd.MM.yyyy HH:mm:ss
        const [datePart, timePart = "00:00:00"] = dateStr.split(" ");
        const [day, month, year] = datePart.split(".");
        const [hours, minutes, seconds = "00"] = timePart.split(":");

        // ✅ استخدم أرقام مباشرة في الـ constructor (local time)
        d = new Date(
          Number(year),
          Number(month) - 1,   // ⚠️ الشهر يبدأ من 0
          Number(day),
          Number(hours),
          Number(minutes),
          Number(seconds)
        );
      } else if (typeof dateStr === "string" && dateStr.includes("T")) {
        // ISO بدون timezone → نتعامل معاه كـ local
        d = new Date(dateStr);
      } else {
        d = new Date(dateStr);
      }

      if (isNaN(d.getTime())) return dateStr;

      const now = new Date();

      // ── فرق الأيام ─────────────────────────────────────────
      const startOfDay = (date) =>
        new Date(date.getFullYear(), date.getMonth(), date.getDate());

      const diffDays = Math.floor(
        (startOfDay(now) - startOfDay(d)) / (1000 * 60 * 60 * 24)
      );

      // ── نفس اليوم: الوقت فقط ────────────────────────────────
      if (diffDays === 0) {
        return d.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      // ── أمس ────────────────────────────────────────────────
      if (diffDays === 1) {
        return t("chat.yesterday", "Gestern");
      }

      // ── خلال آخر 7 أيام: اسم اليوم ─────────────────────────
      if (diffDays < 7) {
        return d.toLocaleDateString([], { weekday: "short" });
      }

      // ── أقدم من كده: التاريخ الكامل ────────────────────────
      return d.toLocaleDateString([], {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    } catch (err) {
      console.error("formatTime error:", err);
      return dateStr;
    }
  };

  const getPreviewText = (conv) => {
    const offer = parseOfferCard(conv.lastMessage?.message);
    if (offer) {
      if (offer.status === "rejected") {
        return t(
          "chat.offer_rejected_preview",
          "Der Geschäftspartner lehnt Ihr Angebot ab",
        );
      }
      if (offer.status === "accepted") {
        return t("chat.offer_accepted_preview", "Angebot angenommen");
      }
      return t("chat.offer_sent_preview", "Angebot gesendet") +
        (offer.price ? `: ${offer.price} EUR` : "");
    }
    if (conv.lastMessage?.has_attachments) return "📎 Attachment";
    const text = conv.lastMessage?.message || "";
    return text.length > 60 ? text.slice(0, 60) + "…" : text;
  };

  const resolveStatusKey = (conv) => {
    if (conv.offerStatus) return conv.offerStatus;
    if ((ordersUnreadCounts[conv.orderId] ?? conv.unreadCount) > 0) {
      return "inquiry";
    }
    return "default";
  };

  if (loading) {
    return (
      <>
        <NavbarMenu />
        <div className="dark-table-container">
          <Container fluid>
            <div className="dark-loading">
              <Spinner
                animation="border"
                style={{ color: "var(--orange-primary)" }}
              />
              <p className="mt-2" style={{ color: "var(--text-muted)" }}>
                {t("invoices.general.loading") || "Loading..."}
              </p>
            </div>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <NavbarMenu />
      <div className="dark-table-container">
        <Container fluid>
          <div className="dark-table-card p-3 p-md-4">
            <h4
              className="page-title mb-4"
              style={{ fontSize: "1.8rem" }}
            >
              {t("general.order_chat") || "Nachrichten"}
            </h4>

            {error && (
              <Alert variant="danger" className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                {error}
              </Alert>
            )}

            {/* Tabs: Alle / Ungelesen */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <button
                type="button"
                onClick={() => setFilter("all")}
                className="border-0 d-flex align-items-center gap-2 px-3 py-2"
                style={{
                  borderRadius: 999,
                  background: filter === "all" ? "#0d9488" : "#f1f5f9",
                  color: filter === "all" ? "#fff" : "#475569",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                <FaComments size={14} />
                Alle
                <span
                  className="rounded-pill px-2"
                  style={{
                    background:
                      filter === "all" ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                    fontSize: 12,
                  }}
                >
                  {conversations.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setFilter("unread")}
                className="border-0 d-flex align-items-center gap-2 px-3 py-2"
                style={{
                  borderRadius: 999,
                  background: filter === "unread" ? "#0d9488" : "#f1f5f9",
                  color: filter === "unread" ? "#fff" : "#475569",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Ungelesen
                <span
                  className="rounded-pill px-2"
                  style={{
                    background:
                      filter === "unread"
                        ? "rgba(255,255,255,0.25)"
                        : "#e2e8f0",
                    fontSize: 12,
                  }}
                >
                  {unreadTotal}
                </span>
              </button>
            </div>

            {/* Search */}
            <InputGroup className="mb-3" style={{ maxWidth: "100%" }}>
              <InputGroup.Text className="bg-white border-end-0">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                className="border-start-0"
                placeholder={t("invoices.search.placeholder") || "Suchen..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm("")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>

            {/* List */}
            {filteredConversations.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <FaComments size={40} style={{ opacity: 0.35 }} />
                <p className="mt-2 mb-0">
                  {searchTerm
                    ? t("no_results") || "Keine Ergebnisse"
                    : t("no_messages") || "Keine Nachrichten"}
                </p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {filteredConversations.map((conv) => {
                  const unread =
                    ordersUnreadCounts[conv.orderId] ?? conv.unreadCount;
                  const name =
                    conv.otherParty?.name || t("unknown_user") || "Unknown";
                  const initial = name
                    .split(" ")
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();

                  const statusKey = resolveStatusKey(conv);
                  const meta = STATUS_META[statusKey] || STATUS_META.default;
                  const StatusIcon = meta.icon;
                  const preview = getPreviewText(conv);

                  const fromLabel = shortPlace(conv.from);
                  const toLabel = shortPlace(conv.to);

                  return (
                    <div
                      key={conv.orderId}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenChat(conv)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleOpenChat(conv);
                      }}
                      className="d-flex gap-3 p-3"
                      style={{
                        background: meta.bg,
                        border: `1px solid ${meta.border}`,
                        borderRadius: 16,
                        cursor: "pointer",
                        transition: "box-shadow .15s",
                      }}
                    >
                      {/* Avatar */}
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
                        style={{
                          width: 48,
                          height: 48,
                          background:
                            statusKey === "rejected"
                              ? "#f87171"
                              : statusKey === "accepted"
                                ? "#34d399"
                                : "#3b82f6",
                          fontSize: 15,
                        }}
                      >
                        {initial}
                      </div>

                      {/* Body */}
                      <div className="flex-grow-1 min-width-0">
                        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                          <div className="min-width-0">
                            <div
                              className="fw-bold text-truncate"
                              style={{ color: "#0f172a", fontSize: 15 }}
                            >
                              {name}
                            </div>
                            <div
                              className="text-truncate"
                              style={{
                                color:
                                  statusKey === "rejected"
                                    ? "#dc2626"
                                    : "#64748b",
                                fontSize: 13,
                              }}
                            >
                              {preview}
                            </div>
                            {conv.company && (
                              <div
                                className="text-truncate"
                                style={{ color: "#94a3b8", fontSize: 12 }}
                              >
                                {conv.company}
                              </div>
                            )}
                          </div>

                          <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                            <span
                              className="d-inline-flex align-items-center gap-1 px-2 py-1"
                              style={{
                                borderRadius: 999,
                                background: "#fff",
                                color: meta.color,
                                fontSize: 11,
                                fontWeight: 600,
                                border: `1px solid ${meta.border}`,
                              }}
                            >
                              <StatusIcon size={11} />
                              {meta.label}
                            </span>
                            <span style={{ color: "#94a3b8", fontSize: 12 }}>
                              {formatTime(conv.lastMessage?.created_at)}
                            </span>
                            {unread > 0 && (
                              <span
                                className="d-inline-flex align-items-center justify-content-center text-white fw-bold"
                                style={{
                                  minWidth: 22,
                                  height: 22,
                                  borderRadius: 999,
                                  background: "#0d9488",
                                  fontSize: 11,
                                  padding: "0 6px",
                                }}
                              >
                                {unread}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                          {/* Route */}
                          {(fromLabel !== "—" || toLabel !== "—") && (
                            <div
                              className="d-inline-flex align-items-center gap-2 mt-2 px-2 py-1 flex-wrap"
                              style={{
                                background: "rgba(255,255,255,0.75)",
                                borderRadius: 8,
                                fontSize: 12,
                                color: "#475569",
                              }}
                            >
                              <FaTruck size={12} style={{ opacity: 0.7 }} />
                              <span className="text-truncate" style={{ maxWidth: 140 }}>
                                {fromLabel}
                              </span>
                              <span style={{ opacity: 0.5 }}>→</span>
                              <span className="text-truncate" style={{ maxWidth: 140 }}>
                                {toLabel}
                              </span>
                            </div>
                          )}

                          <span 
                            style={{
                              background: "rgba(255,255,255,0.75)",
                              borderRadius: 8,
                              fontSize: 12,
                              color: "#475569",
                            }}
                          >
                            {conv?.bookingId}
                          </span>
                        </div>

                      </div>

                      <div
                        className="d-flex align-items-center text-muted flex-shrink-0"
                        style={{ fontSize: 18 }}
                      >
                        ›
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      </div>

      <DriverChatModal
        show={showOrder}
        handleClose={() => {
          setShowOrder(false);
          setSelectedOrder(null);
          fetchTransportsChat();
        }}
        // ✅ المرور الصحيح
        transportId={selectedOrder?.transport_id || selectedOrder?.transport?.id}
        orderId={selectedOrder?.id || selectedOrder?.transport_order_id}
        order={selectedOrder}
        unreadCount={
          selectedOrder ? ordersUnreadCounts[selectedOrder.id] || 0 : 0
        }
        onMessagesRead={(orderId) => updateOrderUnreadCount(orderId, 0)}
        orderStatus={selectedOrder?.status}
      />

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}

      <Footer />
    </>
  );
}